import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    // Modal de pesquisa / resource-card: o link pode não ter ref; não bloquear o card.
    const link =
      this.refs.productCardLink ||
      this.querySelector('a.resource-card__link, a.product-card__link');
    if (link instanceof HTMLAnchorElement && !this.refs.productCardLink) {
      this.refs.productCardLink = link;
    }
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages(event);
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with product-price containers (price, installments, etc.).
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const html = event.detail.data.html;
    const currentPrices = this.querySelectorAll('product-price');
    let updatedByBlockId = false;

    currentPrices.forEach((priceEl) => {
      if (!(priceEl instanceof HTMLElement) || this.#isSecondaryPrice(priceEl)) return;

      const blockId = priceEl.dataset.blockId;
      const currentContainer = priceEl.querySelector('[ref="priceContainer"]');
      if (!currentContainer || !blockId) return;

      const newContainer = html.querySelector(
        `product-price[data-block-id="${blockId}"] [ref="priceContainer"]`
      );
      if (newContainer) {
        morph(currentContainer, newContainer);
        updatedByBlockId = true;
      }
    });

    if (updatedByBlockId) return;

    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);
    if (!newPriceElement) return;

    for (const priceEl of currentPrices) {
      if (!(priceEl instanceof HTMLElement) || this.#isSecondaryPrice(priceEl)) continue;

      const priceContainer = priceEl.querySelector(`[ref='priceContainer']`);
      if (!priceContainer) continue;

      morph(priceContainer, newPriceElement.cloneNode(true));
    }
  }

  /**
   * Parcelamento e Pix não são o preço. A seção do card só devolve o valor,
   * e trocar esses blocos duplica o preço e apaga o texto de parcelas.
   * @param {HTMLElement} priceEl
   */
  #isSecondaryPrice(priceEl) {
    return (
      priceEl.classList.contains('card-installments-block') ||
      priceEl.classList.contains('installments-block') ||
      priceEl.classList.contains('pix-simplificado-block') ||
      priceEl.classList.contains('jv-pix')
    );
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Shows the selected color photo.
   * The card only keeps one or two slides, so a color missing from the slideshow
   * replaces the visible image instead of hiding it.
   * @param {VariantUpdateEvent} [event]
   */
  #updateVariantImages(event) {
    const { slideshow } = this.refs;

    if (!this.variantPicker?.selectedOption) return;

    this.#clearHoverImage();

    const selectedImageId = this.variantPicker.selectedOption.dataset.optionMediaId;
    const previewSrc = event?.detail?.resource?.featured_media?.preview_image?.src;

    if (!slideshow) {
      this.#updateResourceCardImages(previewSrc);
      return;
    }

    if (!selectedImageId) return;

    const slides = [...(slideshow.refs.slides ?? [])];
    const targetSlide = slides.find((slide) => slide.getAttribute('slide-id') == selectedImageId);
    const nextSrc =
      previewSrc ||
      targetSlide?.querySelector('img')?.currentSrc ||
      targetSlide?.querySelector('img')?.src;

    // Fade cards only keep the first slide in normal flow. Selecting the hover
    // slide (Nude is often media #2) pulls that slide out of flow and the photo collapses.
    if (slideshow.dataset.transitionStyle === 'fade') {
      const heightSlide = slides[0];
      const image = heightSlide?.querySelector('img');

      if (!(heightSlide instanceof HTMLElement) || !(image instanceof HTMLImageElement) || !nextSrc) return;

      heightSlide.hidden = false;
      heightSlide.classList.remove('slideshow-slide--fade-out');

      for (const slide of slides) {
        slide.classList.remove('slideshow-slide--fade-out');
        slide.setAttribute('aria-hidden', slide === heightSlide ? 'false' : 'true');
      }

      this.#swapCardImage(image, nextSrc);
      heightSlide.setAttribute('slide-id', selectedImageId);
      return;
    }

    if (targetSlide) {
      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide !== targetSlide;
      }

      slideshow.select({ id: selectedImageId }, undefined, this.#slideMotion(slideshow));
      return;
    }

    if (!nextSrc) return;

    const visibleSlide = slides.find((slide) => !slide.hidden) ?? slides[0];
    const image = visibleSlide?.querySelector('img');

    if (!(image instanceof HTMLImageElement) || !visibleSlide) return;

    this.#swapCardImage(image, nextSrc);
    visibleSlide.setAttribute('slide-id', selectedImageId);
  }

  /**
   * Card compacto (modal de pesquisa): troca a foto principal e a de hover pela cor.
   * @param {string | undefined} previewSrc
   */
  #updateResourceCardImages(previewSrc) {
    const primary = this.querySelector('.resource-card__image:not(.resource-card__image--secondary)');
    const secondary = this.querySelector('.resource-card__image--secondary');
    const hoverSrc = this.variantPicker?.selectedOption?.dataset.hoverMediaSrc;
    const resourceCard = this.querySelector('.resource-card');

    if (primary instanceof HTMLImageElement && previewSrc) {
      this.#swapCardImage(primary, previewSrc);
    }

    if (secondary instanceof HTMLImageElement) {
      if (hoverSrc) {
        this.#applyCardImage(secondary, this.#buildCardImageSources(secondary, hoverSrc));
        secondary.hidden = false;
        resourceCard?.classList.remove('resource-card--no-color-hover');
      } else {
        secondary.hidden = true;
        resourceCard?.classList.add('resource-card--no-color-hover');
      }
    }
  }

  /**
   * @param {HTMLImageElement} image
   * @param {string} src
   */
  #swapCardImage(image, src) {
    const next = this.#buildCardImageSources(image, src);
    const currentSrc = image.currentSrc || image.src;

    if (this.#isSameCardImage(currentSrc, next.src)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const parent = image.parentElement;

    if (reduceMotion || !(parent instanceof HTMLElement)) {
      this.#applyCardImage(image, next);
      return;
    }

    parent.querySelector('.card-gallery__crossfade')?.remove();

    const overlay = /** @type {HTMLImageElement} */ (image.cloneNode(false));
    // Sem as classes do resource-card: a opacity do card anula o fade do overlay.
    overlay.className = 'card-gallery__crossfade';
    overlay.alt = '';
    overlay.setAttribute('aria-hidden', 'true');
    this.#applyCardImage(overlay, next);
    parent.append(overlay);

    let settled = false;
    const finish = () => {
      if (settled || !overlay.isConnected) return;
      settled = true;
      this.#applyCardImage(image, next);
      overlay.remove();
    };

    const reveal = () => {
      overlay.classList.add('is-shown');
      window.setTimeout(finish, 520);
    };

    overlay.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'opacity') finish();
      },
      { once: true }
    );
    overlay.addEventListener('error', finish, { once: true });

    if (overlay.complete && overlay.naturalWidth > 0) reveal();
    else overlay.addEventListener('load', reveal, { once: true });
  }

  /**
   * @param {HTMLImageElement} image
   * @param {string} src
   */
  #buildCardImageSources(image, src) {
    const widths = [];
    const srcset = image.getAttribute('srcset') || '';

    for (const part of srcset.split(',')) {
      const match = part.match(/width=(\d+)/);

      if (match) widths.push(Number(match[1]));
    }

    const uniqueWidths = widths.length > 0 ? [...new Set(widths)] : [800];
    const currentMatch = (image.currentSrc || image.src).match(/width=(\d+)/);
    const currentWidth = currentMatch ? Number(currentMatch[1]) : uniqueWidths[0];

    return {
      srcset: uniqueWidths.map((width) => `${this.#imageUrlWithWidth(src, width)} ${width}w`).join(', '),
      src: this.#imageUrlWithWidth(src, currentWidth),
      max: image.hasAttribute('data-max-resolution') ? this.#imageUrlWithWidth(src, 3840) : '',
    };
  }

  /**
   * @param {HTMLImageElement} image
   * @param {{srcset: string, src: string, max: string}} next
   */
  #applyCardImage(image, next) {
    image.srcset = next.srcset;
    image.src = next.src;

    if (next.max) image.setAttribute('data-max-resolution', next.max);
  }

  /**
   * @param {string} currentSrc
   * @param {string} nextSrc
   */
  #isSameCardImage(currentSrc, nextSrc) {
    try {
      const current = new URL(currentSrc, window.location.origin);
      const next = new URL(nextSrc, window.location.origin);
      current.searchParams.delete('width');
      next.searchParams.delete('width');
      return current.pathname === next.pathname;
    } catch {
      return false;
    }
  }

  /**
   * @param {import('slideshow').Slideshow} slideshow
   */
  #slideMotion(slideshow) {
    return { animate: slideshow.dataset.transitionStyle === 'fade' };
  }

  /**
   * @param {string} src
   * @param {number} width
   */
  #imageUrlWithWidth(src, width) {
    const url = new URL(src, window.location.origin);

    url.searchParams.set('width', String(width));
    return url.toString();
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, this.#slideMotion(slideshow));
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (slideshow.dataset.transitionStyle === 'fade') {
      this.#previewHoverImage(slideshow);
      return;
    }

    this.#advanceHoverSlide(slideshow);
  }

  /**
   * Segunda imagem por cima da atual. Não muda o slide: trocar o slide
   * altera position e a foto salta.
   * @param {import('slideshow').Slideshow} slideshow
   */
  #previewHoverImage(slideshow) {
    const slides = [...(slideshow.refs.slides ?? [])].filter((slide) => !slide.hidden);
    const baseImage = slides[0]?.querySelector('img');

    if (!(baseImage instanceof HTMLImageElement) || !(baseImage.parentElement instanceof HTMLElement)) return;

    const selectedColor = this.variantPicker?.selectedOption;
    // Com cor escolhida: só a 2.ª foto dessa cor. Sem mapa, não há hover.
    // Sem cor escolhida: mantém a 2.ª mídia genérica do card (slide 2).
    if (selectedColor) {
      const colorHoverSrc = selectedColor.dataset.hoverMediaSrc;
      if (!colorHoverSrc) return;
      if (this.#isSameCardImage(baseImage.currentSrc || baseImage.src, colorHoverSrc)) return;

      const parent = baseImage.parentElement;
      parent.querySelector('.card-gallery__crossfade--hover')?.remove();

      const overlay = /** @type {HTMLImageElement} */ (baseImage.cloneNode(false));
      overlay.className = 'card-gallery__crossfade card-gallery__crossfade--hover';
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      this.#applyCardImage(overlay, this.#buildCardImageSources(baseImage, colorHoverSrc));
      parent.append(overlay);
      this.#revealHoverOverlay(overlay);
      return;
    }

    const hoverImage = slides[1]?.querySelector('img');

    if (!(hoverImage instanceof HTMLImageElement)) return;
    if (this.#isSameCardImage(baseImage.currentSrc || baseImage.src, hoverImage.currentSrc || hoverImage.src)) return;

    const parent = baseImage.parentElement;
    parent.querySelector('.card-gallery__crossfade--hover')?.remove();

    const overlay = /** @type {HTMLImageElement} */ (hoverImage.cloneNode(false));
    overlay.className = 'card-gallery__crossfade card-gallery__crossfade--hover';
    overlay.alt = '';
    overlay.setAttribute('aria-hidden', 'true');
    parent.append(overlay);
    this.#revealHoverOverlay(overlay);
  }

  /**
   * @param {HTMLImageElement} overlay
   */
  #revealHoverOverlay(overlay) {
    const reveal = () => overlay.classList.add('is-shown');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || (overlay.complete && overlay.naturalWidth > 0)) {
      reveal();
      return;
    }

    overlay.addEventListener('load', reveal, { once: true });
  }

  /**
   * Tira a segunda imagem do hover sem mexer na foto de baixo.
   */
  #clearHoverImage() {
    const overlay = this.querySelector('.card-gallery__crossfade--hover');

    if (!(overlay instanceof HTMLImageElement)) return;

    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !overlay.classList.contains('is-shown')
    ) {
      overlay.remove();
      return;
    }

    overlay.classList.remove('is-shown');

    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      overlay.remove();
    };

    overlay.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'opacity') remove();
      },
      { once: true }
    );
    window.setTimeout(remove, 520);
  }

  /**
   * @param {import('slideshow').Slideshow} slideshow
   */
  #advanceHoverSlide(slideshow) {
    const motion = this.#slideMotion(slideshow);

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, motion);
      return;
    }

    slideshow.next(undefined, motion);
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (slideshow?.dataset.transitionStyle === 'fade') {
      this.#clearHoverImage();
      return;
    }

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, this.#slideMotion(slideshow));
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, this.#slideMotion(slideshow));
        return;
      }
    }

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, this.#slideMotion(slideshow));
      return;
    }

    slideshow.previous(undefined, this.#slideMotion(slideshow));
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Snapshot visual swatch styles before morph (section re-render can drop HEX).
   * @returns {{id: string, style: string, className: string}[]}
   */
  #snapshotSwatchStyles() {
    /** @type {{id: string, style: string, className: string}[]} */
    const snapshots = [];

    for (const input of this.querySelectorAll('input[data-option-value-id]')) {
      if (!(input instanceof HTMLInputElement) || !input.dataset.optionValueId) continue;
      const swatch = input.parentElement?.querySelector('.swatch');
      if (!(swatch instanceof HTMLElement)) continue;

      snapshots.push({
        id: input.dataset.optionValueId,
        style: swatch.getAttribute('style') || '',
        className: swatch.className,
      });
    }

    return snapshots;
  }

  /**
   * Restore swatch backgrounds after morph.
   * @param {{id: string, style: string, className: string}[]} snapshots
   */
  #restoreSwatchStyles(snapshots) {
    for (const snap of snapshots) {
      const input = this.querySelector(`input[data-option-value-id="${snap.id}"]`);
      if (!(input instanceof HTMLInputElement)) continue;
      const swatch = input.parentElement?.querySelector('.swatch');
      if (!(swatch instanceof HTMLElement)) continue;

      if (snap.style) swatch.setAttribute('style', snap.style);
      if (snap.className) swatch.className = snap.className;
    }
  }

  /**
   * Preserve custom Cor/HEX swatches across section-rendering morph.
   * @param {Document} newHtml
   */
  updateVariantPicker(newHtml) {
    const snapshots = this.#snapshotSwatchStyles();
    const newProduct = VariantPicker.prototype.updateVariantPicker.call(this, newHtml);
    this.#restoreSwatchStyles(snapshots);
    return newProduct;
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Expande overflow-list (+N) para mostrar todas as amostras.
   * @param {Event} [event]
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
